/**
 * An immutable, type-safe map keyed by branded string types.
 *
 * Works around TypeScript's limitation where branded string intersections
 * (e.g. `'ctv' & BRAND<'PackageSource'>`) cannot index Record or object types.
 * See: https://github.com/microsoft/TypeScript/issues/42790
 *
 * @example
 * const config = new BrandedMap<PackageSource, Config>({
 *   ctv: { ... },
 *   linkedin: { ... },
 * })
 *
 * const value = config.get(packageSource) // requires branded key, returns Config
 */
export class BrandedMap<K extends string, V> {
  private readonly data: ReadonlyMap<string, V>
  readonly size: number

  constructor(entries: Record<string, V>) {
    this.data = new Map(Object.entries(entries))
    this.size = this.data.size
  }

  get(key: K): V {
    if (!this.data.has(key)) {
      throw new Error(`BrandedMap: key not found: ${key}`)
    }
    return this.data.get(key) as V
  }

  has(key: K): boolean {
    return this.data.has(key)
  }

  keys(): K[] {
    return [...this.data.keys()] as K[]
  }

  values(): V[] {
    return [...this.data.values()]
  }

  entries(): [K, V][] {
    return [...this.data.entries()] as [K, V][]
  }

  forEach(fn: (value: V, key: K) => void): void {
    this.data.forEach((value, key) => {
      fn(value, key as K)
    })
  }

  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this.data[Symbol.iterator]() as IterableIterator<[K, V]>
  }

  map<U>(fn: (value: V, key: K) => U): BrandedMap<K, U> {
    const result: Record<string, U> = {}
    this.data.forEach((value, key) => {
      result[key] = fn(value, key as K)
    })
    return new BrandedMap<K, U>(result)
  }

  /**
   * Build a BrandedMap by iterating over raw keys and calling a selector
   * with branded values.
   *
   * @example
   * const billing = BrandedMap.build<PackageSource, Config>(
   *   PackageSources,
   *   (source) => configMap.get(source).billingMethods
   * )
   */
  static build<K extends string, V>(
    keys: readonly string[],
    selector: (key: K) => V
  ): BrandedMap<K, V> {
    const entries: Record<string, V> = {}
    for (const raw of keys) {
      entries[raw] = selector(raw as K)
    }
    return new BrandedMap<K, V>(entries)
  }

  static fromEntries<K extends string, V>(
    entries: Iterable<readonly [string, V]>
  ): BrandedMap<K, V> {
    return new BrandedMap<K, V>(
      Object.fromEntries(entries) as Record<string, V>
    )
  }
}
